import EclecticWidget from "@/components/chat/floating/eclectic";
import ChatSelectorButton from "@/components/chat/list/chatSelectorButton";
import Messages from "@/components/chat/messages";
import { useChatContext } from "@/contexts/chatContext";
import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { AnimatePresence, Motion } from "@legendapp/motion";
import Show from "@repo/components/src/shared/show";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Divider } from "@repo/components/src/ui/divider";
import { HStack } from "@repo/components/src/ui/hstack";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  ChatsDocument,
  ChatsQuery,
  GetChatDocument,
} from "@repo/graphql/documents/chat/query.generated";
import { ArrowLeft, X } from "lucide-react-native";
import React, { useCallback, useMemo } from "react";
import { FlatList, ListRenderItem } from "react-native";

const openEclectic = () => {
  EclecticWidget.open();
};

type Item = ChatsQuery["chat"]["chats"]["edges"][number];

type FloatingChatPanelProps = {
  brandId: string;
  onClose: () => void;
};

const LoadingChats = () => (
  <HStack space="md" className="items-center py-2 p-2">
    <Skeleton variant="circular" className="h-10 w-10" />
    <VStack space="sm" className="flex-1">
      <Skeleton variant="rounded" className="h-3 w-3/4" />
      <Skeleton variant="rounded" className="h-3 w-1/4" />
    </VStack>
  </HStack>
);

const ChatList = React.memo((props: { brandId: string }) => {
  const { data, networkStatus, fetchMore } = useQuery(ChatsDocument, {
    variables: {
      first: 30,
      brand_Id: props.brandId,
      orderBy: "-isPinnedByBrand,-lastMessageSent,-id",
    },
  });

  const pageInfo = data?.chat.chats.pageInfo;

  const sortedEdges = useMemo(() => {
    return data?.chat.chats.edges.slice().sort((a, b) => {
      const aChat = a.node;
      const bChat = b.node;
      if (aChat.isPinnedByBrand && !bChat.isPinnedByBrand) return -1;
      if (!aChat.isPinnedByBrand && bChat.isPinnedByBrand) return 1;
      return aChat.lastMessageSent < bChat.lastMessageSent ? 1 : -1;
    });
  }, [data]);

  const renderItem: ListRenderItem<Item> = useCallback((info) => {
    return <ChatSelectorButton edge={info.item} />;
  }, []);

  const keyExtractor = useCallback((item: Item) => {
    return `FloatingChat:${item.node.id}`;
  }, []);

  const onEndReached = useCallback(() => {
    pageInfo?.hasNextPage &&
      fetchMore({ variables: { after: pageInfo.endCursor } });
  }, [pageInfo, fetchMore]);

  return (
    <FlatList
      className="flex-1"
      contentContainerClassName="gap-y-1 p-2"
      data={sortedEdges}
      renderItem={renderItem}
      showsHorizontalScrollIndicator={false}
      keyExtractor={keyExtractor}
      showsVerticalScrollIndicator={false}
      onEndReached={onEndReached}
      onEndReachedThreshold={0.2}
      ListFooterComponent={
        <Show when={networkStatus === NetworkStatus.fetchMore}>
          <LoadingChats />
        </Show>
      }
      ListEmptyComponent={
        <Show
          when={[NetworkStatus.setVariables, NetworkStatus.loading].includes(
            networkStatus,
          )}
          fallback={
            <Text className="text-center mt-8">No hay chats disponibles</Text>
          }
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <LoadingChats key={index} />
          ))}
        </Show>
      }
    />
  );
});

ChatList.displayName = "ChatList";

const FloatingChatPanel = React.memo((props: FloatingChatPanelProps) => {
  const { brandId, onClose } = props;
  const { selectedChatId, setSelectedChatId } = useChatContext();

  const { data: chatData } = useQuery(GetChatDocument, {
    variables: { id: selectedChatId! },
    skip: !selectedChatId,
  });

  const chatUser = useMemo(
    () => chatData?.chat.get.chatparticipantSet[0]?.user ?? null,
    [chatData],
  );
  const chatUserName = useMemo(() => {
    if (!chatUser) return null;
    return `${chatUser.firstName} ${chatUser.lastName}`.trim();
  }, [chatUser]);

  const goBack = useCallback(() => {
    setSelectedChatId(null);
  }, [setSelectedChatId]);

  const handleHelpButton = useCallback(() => {
    openEclectic();
    onClose();
  }, [onClose]);

  return (
    <Box className="flex-1 h-full bg-background-0">
      <HStack
        className="items-center pl-5 pr-4 py-3 bg-background-50"
        space="sm"
      >
        {selectedChatId && (
          <>
            <Box className="w-8">
              <Button onPress={goBack} variant="link" className="p-0" size="sm">
                <ButtonIcon as={ArrowLeft} />
              </Button>
            </Box>
            {chatUser && (
              <Avatar size="sm">
                <AvatarFallbackText>
                  {chatUserName ?? chatUser.firstName ?? "?"}
                </AvatarFallbackText>
                <AvatarImage source={{ uri: chatUser.profilePicture?.url }} />
              </Avatar>
            )}
          </>
        )}
        <Text size="lg" bold className="flex-1" numberOfLines={1}>
          {chatUserName ?? "Chat"}
        </Text>
        <Button
          onPress={handleHelpButton}
          variant="link"
          size="sm"
          className="p-0"
        >
          <ButtonText className="text-typography-500">Ayuda</ButtonText>
        </Button>
        <Button onPress={onClose} variant="link" className="p-0" size="sm">
          <ButtonIcon as={X} />
        </Button>
      </HStack>
      <Divider className="bg-outline-100" />
      <Box className="flex-1 overflow-hidden relative bg-gradient-to-b from-brand-50/60 via-background-0 to-background-0">
        <AnimatePresence>
          {selectedChatId ? (
            <Motion.View
              key="messages"
              initial={{ x: 480 }}
              animate={{ x: 0 }}
              exit={{ x: 480 }}
              transition={{
                type: "spring",
                damping: 28,
                stiffness: 320,
              }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
              }}
            >
              <Messages showHeader={false} />
            </Motion.View>
          ) : (
            <Motion.View
              key="list"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <ChatList brandId={brandId} />
            </Motion.View>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
});

FloatingChatPanel.displayName = "FloatingChatPanel";

export default FloatingChatPanel;
